import { deleteApi, getApi, postApi } from "../helpers/auth_helpers";
import type {
  OrganizationTaxonomyResponse,
  OrganizationTaxonomyUpsertRequest,
  ProcessFieldMapping,
  ProcessFieldMappingUpsertRequest,
  ResolvedFieldConfig,
  TaxonomyDefinition,
  TaxonomyEnvelope,
  TaxonomyListQuery,
  TaxonomyUpsertRequest,
  TaxonomyUsedIn,
} from "./types";

const base = () => (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");

async function request<T>(live: () => Promise<any>): Promise<TaxonomyEnvelope<T>> {
  try {
    const res = await live();
    if (res && typeof res.success === "boolean") return res;
  } catch {
    // Keep network/invalid-response failures visible; never simulate a successful save.
  }
  return { success: false, message: "Custom fields could not be loaded or saved. Please try again." };
}

export async function listTaxonomyDefinitions(
  query?: TaxonomyListQuery
): Promise<TaxonomyEnvelope<TaxonomyDefinition[]>> {
  const params = new URLSearchParams();
  if (query?.search) params.set("search", query.search);
  if (query?.usedIn) params.set("usedIn", query.usedIn);
  if (query?.fieldType) params.set("fieldType", query.fieldType);
  if (query?.source) params.set("source", query.source);
  const qs = params.toString();
  return request(
    () => getApi(`${base()}/taxonomy-master/list${qs ? `?${qs}` : ""}`)
  );
}

export async function upsertTaxonomyDefinition(
  body: TaxonomyUpsertRequest
): Promise<TaxonomyEnvelope<TaxonomyDefinition>> {
  return request(
    () => postApi(`${base()}/taxonomy-master/upsert`, body)
  );
}

export async function removeTaxonomyDefinition(id: string): Promise<TaxonomyEnvelope<null>> {
  return request(
    () => deleteApi(`${base()}/taxonomy-master/remove?id=${encodeURIComponent(id)}`)
  );
}

export async function listOrganizationTaxonomy(): Promise<TaxonomyEnvelope<ResolvedFieldConfig[]>> {
  const res = await request<OrganizationTaxonomyResponse[]>(
    () => getApi(`${base()}/organization-taxonomy/list`)
  );
  if (!res.success) return { success: false, message: res.message };
  return {
    ...res,
    data: (res.data || []).filter((row) => row.field).map(({ field, ...config }) => ({
      ...field,
      ...config,
      enabled: config.enabled && field.active !== false,
      visibility: config.visibility === "PUBLIC" ? "PUBLIC" : "INTERNAL",
    })),
  };
}

export async function upsertOrganizationTaxonomy(
  body: OrganizationTaxonomyUpsertRequest
): Promise<TaxonomyEnvelope<OrganizationTaxonomyResponse>> {
  return request(
    () => postApi(`${base()}/organization-taxonomy/upsert`, body)
  );
}

export async function removeOrganizationTaxonomy(id: string): Promise<TaxonomyEnvelope<null>> {
  return request(
    () => deleteApi(`${base()}/organization-taxonomy/remove?id=${encodeURIComponent(id)}`)
  );
}

export async function getOrganizationTaxonomyConfig(
  context: Extract<TaxonomyUsedIn, "PRODUCT" | "PROCESS">
): Promise<TaxonomyEnvelope<ResolvedFieldConfig[]>> {
  const res = await request<ResolvedFieldConfig[]>(
    () => getApi(`${base()}/organization-taxonomy/config?context=${context}`)
  );
  return {
    ...res,
    data: (res.data || []).map((field) => ({
      ...field,
      visibility: field.visibility === "PUBLIC" ? "PUBLIC" : "INTERNAL",
    })),
  };
}

export async function resolveTaxonomyDefinitions(
  ids: string[]
): Promise<TaxonomyEnvelope<TaxonomyDefinition[]>> {
  const unique = Array.from(new Set((ids || []).filter(Boolean)));
  if (!unique.length) return { success: true, data: [] };
  return request(() => getApi(`${base()}/taxonomy-master/by-ids?ids=${encodeURIComponent(unique.join(","))}`));
}

export async function listProcessFieldMappingsByProcess(
  processMasterId: string
): Promise<TaxonomyEnvelope<ProcessFieldMapping[]>> {
  return request(() => getApi(
    `${base()}/process-field-mapping/by-process?processMasterId=${encodeURIComponent(processMasterId)}`
  ));
}

export async function listProcessFieldMappingsByField(
  taxonomyId: string
): Promise<TaxonomyEnvelope<ProcessFieldMapping[]>> {
  return request(() => getApi(
    `${base()}/process-field-mapping/by-field?taxonomyId=${encodeURIComponent(taxonomyId)}`
  ));
}

export async function upsertProcessFieldMapping(
  body: ProcessFieldMappingUpsertRequest
): Promise<TaxonomyEnvelope<ProcessFieldMapping>> {
  return request(() => postApi(`${base()}/process-field-mapping/upsert`, body));
}

export async function disableProcessFieldMapping(id: string): Promise<TaxonomyEnvelope<ProcessFieldMapping>> {
  return request(() => postApi(
    `${base()}/process-field-mapping/disable?id=${encodeURIComponent(id)}`,
    {}
  ));
}
